<template>
  <v-card class="d-flex flex-column part-interest-form-card h-100">
    <v-card-title
      class="part-interest-form-title d-flex align-center justify-center pt-4 pb-2 px-4 flex-shrink-0"
    >
      <h2
        class="text-title-large font-weight-medium text-center mb-0 w-100 px-10"
      >
        {{ t(strings.title) }}
      </h2>
      <v-btn
        class="part-interest-form-title__close"
        icon="mdi-close"
        variant="text"
        density="comfortable"
        @click="emit('cancel')"
      />
    </v-card-title>

    <div
      class="part-interest-form-body d-flex flex-column flex-grow-1 overflow-hidden"
      :class="step === 1 ? 'pb-4' : 'pb-0'"
    >
      <v-stepper
        v-model="step"
        flat
        hide-actions
        :editable="false"
        :items="stepperItems"
        class="part-interest-stepper flex-grow-1 d-flex flex-column"
      >
        <template #item.1>
          <v-form
            ref="formStep1Ref"
            class="part-interest-step-form"
            @submit.prevent="onStep1Next"
          >
            <div class="text-body-1 text-high-emphasis mb-3">
              {{ t(strings.fileRequestDescription) }}
            </div>
            <div
              class="part-interest-category-grid"
              role="group"
              :aria-label="t(strings.interestedFilesTitle)"
            >
              <button
                v-for="category in fileCategories"
                :key="category.value"
                type="button"
                class="part-interest-category-btn"
                :class="{
                  'part-interest-category-btn--selected':
                    checkboxValues[category.value],
                }"
                :aria-pressed="checkboxValues[category.value]"
                @click="toggleCategory(category.value)"
              >
                {{ t(category.label) }}
              </button>
            </div>

            <v-textarea
              v-model="message"
              :label="t(strings.messageLabel)"
              :rules="[messageLengthRule]"
              :counter="2000"
              rows="4"
              variant="outlined"
              class="mt-4"
            />
            <v-alert
              v-if="hasRecentSubmission"
              type="warning"
              variant="tonal"
              class="mb-4 mt-4"
              :text="t(strings.recentSubmissionWarning)"
            />

            <v-btn
              type="submit"
              color="primary"
              size="large"
              block
              variant="flat"
              rounded="lg"
              class="text-none font-weight-semibold mt-4"
            >
              {{ t(strings.next) }}
            </v-btn>
          </v-form>
        </template>

        <template #item.2>
          <div class="part-interest-step-form">
            <CompanyProfileForm
              v-if="organization"
              ref="organizationFormRef"
              :organization="organization"
              :render-save-button="false"
              @success="onOrganizationSuccess"
            />
          </div>
        </template>
      </v-stepper>

      <div
        v-if="step === 2"
        class="part-interest-form-footer flex-shrink-0 pa-4"
      >
        <v-btn
          block
          color="primary"
          size="large"
          variant="flat"
          rounded="lg"
          class="text-none font-weight-semibold"
          :loading="mutation.isPending.value || organizationSaving"
          @click="onSubmit"
        >
          {{ t(strings.submitButton) }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { part_interest_form_strings as strings } from "./PartInterestForm.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { useSubmitPartInterestMutation } from "../../requests/messages/buy-interest.ts";
import { partInterestStorage } from "../../storage/part-interest-storage.ts";
import { useGetOrganization } from "../../requests/organizations/get.ts";
import CompanyProfileForm from "../company-profile-form/CompanyProfileForm.vue";
import type { Part, PartFile, Organization } from "@vendata/power-up-spec";
import { eventLogger, traceFromPart } from "@vendata/power-up-clients-common";
import { showInfo, useClientCommon } from "@saflib/vue";

const { t } = useReverseT();

const props = defineProps<{
  part: Part;
  partFiles: PartFile[];
}>();

const emit = defineEmits<{
  success: [];
  cancel: [];
}>();

const step = ref(1);
const formStep1Ref = ref<{
  validate: () => Promise<{ valid: boolean }>;
  reset: () => void;
} | null>(null);
const organizationFormRef = ref();

const message = ref("");

const checkboxValues = ref<Record<string, boolean>>({
  pedigree: false,
  shop_release: false,
  flow_test_report: false,
  dimensional_inspection: false,
  nde_report: false,
  photo: false,
});

const organization = ref<Organization | undefined>(undefined);
const organizationSaving = ref(false);

const organizationQuery = useGetOrganization();
const mutation = useSubmitPartInterestMutation();

const stepperItems = computed(() => [
  { title: t(strings.stepRequestDetails), value: 1 },
  { title: t(strings.stepCompany), value: 2 },
]);

const allFileCategories = [
  { value: "pedigree", label: strings.categoryPedigree },
  { value: "shop_release", label: strings.categoryShopRelease },
  { value: "flow_test_report", label: strings.categoryFlowTestReport },
  {
    value: "dimensional_inspection",
    label: strings.categoryDimensionalInspection,
  },
  { value: "nde_report", label: strings.categoryNdeReport },
  { value: "photo", label: strings.categoryPhoto },
];

const fileCategories = computed(() => {
  const availableCategories = props.partFiles.map((file) => file.category);

  if (availableCategories.length === 0) {
    return allFileCategories;
  }

  return allFileCategories.filter((category) =>
    availableCategories.includes(category.value as any),
  );
});

const interestedFiles = computed(() => {
  return Object.entries(checkboxValues.value)
    .filter(([_, isSelected]) => isSelected)
    .map(([category, _]) => category);
});

const messageLengthRule = (value: string) => {
  return value.length <= 2000 || t(strings.validationMessageTooLong);
};

const hasRecentSubmission = computed(() => {
  return partInterestStorage.hasRecentSubmission(props.part.id, 24 * 7);
});

onMounted(() => {
  eventLogger.emitProductEvent({
    ...useClientCommon("PartInterestForm"),
    event: "contact_form_view",
    context: {
      ...traceFromPart(props.part),
    },
  });
});

watch(
  () => organizationQuery.data.value,
  (newData) => {
    if (newData) {
      organization.value = newData;
    }
  },
  { immediate: true },
);

const onOrganizationSuccess = (updatedProfile: Organization) => {
  organization.value = updatedProfile;
  organizationSaving.value = false;
};

function toggleCategory(categoryValue: string) {
  checkboxValues.value[categoryValue] = !checkboxValues.value[categoryValue];
}

async function onStep1Next() {
  const r = await formStep1Ref.value?.validate();
  if (!r?.valid) return;
  step.value = 2;
}

const onSubmit = async () => {
  try {
    if (organizationFormRef.value) {
      organizationSaving.value = true;
      try {
        await organizationFormRef.value.save();
      } catch (error) {
        console.error("Failed to save organization:", error);
        organizationSaving.value = false;
        return;
      }
    }

    await mutation.mutateAsync({
      partId: props.part.id,
      interestedFiles: interestedFiles.value,
      message: message.value,
    });

    partInterestStorage.addSubmission(props.part.id, "user@example.com");

    eventLogger.emitProductEvent({
      ...useClientCommon("PartInterestForm"),
      event: "contact_form_submit",
      context: {
        ...traceFromPart(props.part),
      },
    });

    showInfo(t(strings.submit_success_snackbar));

    emit("success");

    Object.keys(checkboxValues.value).forEach((key) => {
      checkboxValues.value[key] = false;
    });
    message.value = "";
    step.value = 1;
    formStep1Ref.value?.reset();
  } catch (error) {
    console.error("Failed to submit part interest:", error);
  } finally {
    organizationSaving.value = false;
  }
};
</script>

<style scoped>
.part-interest-form-title {
  position: relative;
}

.part-interest-form-title__close {
  position: absolute;
  inset-inline-end: 0;
  top: 50%;
  transform: translateY(-50%);
}

.part-interest-form-card {
  min-height: 0;
}

.part-interest-form-body {
  min-height: 0;
}

/* Stepper root: utilities set d-flex flex-column; min-height 0 so the window can shrink and scroll */
.part-interest-stepper {
  flex: 1 1 auto;
  min-height: 0;
  background: transparent;
}

/*
 * In a fixed-height bottom sheet, let only the step window scroll (header stays visible).
 * VWindow defaults to overflow: hidden, so we enable vertical scrolling here.
 */
.part-interest-stepper :deep(.v-stepper-window) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.part-interest-stepper :deep(.v-stepper-window .v-window__container) {
  min-height: 0;
}

.part-interest-stepper :deep(.v-stepper-window-item) {
  min-height: 0;
}

.part-interest-step-form {
  padding: 4px 16px 16px;
  box-sizing: border-box;
}

.part-interest-form-footer {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.part-interest-category-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
}

.part-interest-category-btn {
  appearance: none;
  margin: 0;
  cursor: pointer;
  min-height: 56px;
  padding: 12px 10px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.25;
  text-align: center;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    font-weight 0.15s ease;
}

.part-interest-category-btn--selected {
  font-weight: 600;
  border-color: rgb(var(--v-theme-primary));
  background: color-mix(
    in srgb,
    rgb(var(--v-theme-primary)) 14%,
    rgb(var(--v-theme-surface))
  );
  color: rgb(var(--v-theme-on-surface));
}

.part-interest-category-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
